import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface DashboardState {
  // Core filters
  municipalityId: string | null;
  year: number;
  selectedKpiId: string | null;
  governance: string | null;
  compareScope: 'municipality' | 'national';
  
  // UI state
  isAnalysisOpen: boolean;
  isMobileSheetOpen: boolean;
  
  // Cached data keys
  latestYear: number | null;
  dataCache: Map<string, any>;
  
  // Actions
  setMunicipalityId: (id: string | null) => void;
  setYear: (year: number) => void;
  setSelectedKpiId: (kpiId: string | null) => void;
  setGovernance: (governance: string | null) => void;
  setCompareScope: (scope: 'municipality' | 'national') => void;
  setIsAnalysisOpen: (open: boolean) => void;
  setIsMobileSheetOpen: (open: boolean) => void;
  setLatestYear: (year: number | null) => void;
  
  // Cache management
  setCacheData: (key: string, data: any) => void;
  getCacheData: (key: string) => any;
  clearCache: () => void;
  
  // Batch operations
  updateFilters: (filters: Partial<Pick<DashboardState, 'municipalityId' | 'year' | 'selectedKpiId' | 'governance' | 'compareScope'>>) => void;
}

export const useDashboardStore = create<DashboardState>()(
  devtools(
    (set: any, get: any) => ({
      // Initial state
      municipalityId: null,
      year: 2024,
      selectedKpiId: 'N15504', // Default to Meritvärde
      governance: null,
      compareScope: 'municipality',
      isAnalysisOpen: false,
      isMobileSheetOpen: false,
      latestYear: null,
      dataCache: new Map(),
      
      // Actions
      setMunicipalityId: (id: string | null) => set({ municipalityId: id }, false, 'setMunicipalityId'),
      setYear: (year: number) => set({ year }, false, 'setYear'),
      setSelectedKpiId: (kpiId: string | null) => set({ selectedKpiId: kpiId }, false, 'setSelectedKpiId'),
      setGovernance: (governance: string | null) => set({ governance }, false, 'setGovernance'),
      setCompareScope: (scope: 'municipality' | 'national') => set({ compareScope: scope }, false, 'setCompareScope'),
      setIsAnalysisOpen: (open: boolean) => set({ isAnalysisOpen: open }, false, 'setIsAnalysisOpen'),
      setIsMobileSheetOpen: (open: boolean) => set({ isMobileSheetOpen: open }, false, 'setIsMobileSheetOpen'),
      setLatestYear: (year: number | null) => set({ latestYear: year }, false, 'setLatestYear'),
      
      // Cache management
      setCacheData: (key: string, data: any) => {
        const { dataCache } = get();
        const newCache = new Map(dataCache);
        newCache.set(key, data);
        set({ dataCache: newCache }, false, 'setCacheData');
      },
      
      getCacheData: (key: string) => {
        const { dataCache } = get();
        return dataCache.get(key);
      },
      
      clearCache: () => set({ dataCache: new Map() }, false, 'clearCache'),
      
      // Batch operations
      updateFilters: (filters: Partial<Pick<DashboardState, 'municipalityId' | 'year' | 'selectedKpiId' | 'governance' | 'compareScope'>>) => set(filters, false, 'updateFilters'),
    }),
    {
      name: 'dashboard-store',
    }
  )
);

// Selectors for common use cases
export const useSelectedKpi = () => useDashboardStore((state: DashboardState) => state.selectedKpiId);
export const useMunicipalityId = () => useDashboardStore((state: DashboardState) => state.municipalityId);
export const useYear = () => useDashboardStore((state: DashboardState) => state.year);
export const useGovernance = () => useDashboardStore((state: DashboardState) => state.governance);
export const useCompareScope = () => useDashboardStore((state: DashboardState) => state.compareScope);

// Action selectors
export const useDashboardActions = () => {
  const setMunicipalityId = useDashboardStore((state: DashboardState) => state.setMunicipalityId);
  const setYear = useDashboardStore((state: DashboardState) => state.setYear);
  const setSelectedKpiId = useDashboardStore((state: DashboardState) => state.setSelectedKpiId);
  const setGovernance = useDashboardStore((state: DashboardState) => state.setGovernance);
  const setCompareScope = useDashboardStore((state: DashboardState) => state.setCompareScope);
  const setIsAnalysisOpen = useDashboardStore((state: DashboardState) => state.setIsAnalysisOpen);
  const setIsMobileSheetOpen = useDashboardStore((state: DashboardState) => state.setIsMobileSheetOpen);
  const setLatestYear = useDashboardStore((state: DashboardState) => state.setLatestYear);
  const updateFilters = useDashboardStore((state: DashboardState) => state.updateFilters);

  return useMemo(
    () => ({
      setMunicipalityId,
      setYear,
      setSelectedKpiId,
      setGovernance,
      setCompareScope,
      setIsAnalysisOpen,
      setIsMobileSheetOpen,
      setLatestYear,
      updateFilters,
    }),
    [
      setMunicipalityId,
      setYear,
      setSelectedKpiId,
      setGovernance,
      setCompareScope,
      setIsAnalysisOpen,
      setIsMobileSheetOpen,
      setLatestYear,
      updateFilters,
    ]
  );
};

// Cache selectors
export const useCacheActions = () => {
  const setCacheData = useDashboardStore((state: DashboardState) => state.setCacheData);
  const getCacheData = useDashboardStore((state: DashboardState) => state.getCacheData);
  const clearCache = useDashboardStore((state: DashboardState) => state.clearCache);

  return useMemo(
    () => ({
      setCacheData,
      getCacheData,
      clearCache,
    }),
    [setCacheData, getCacheData, clearCache]
  );
};
